import { useMemo, useState } from "react";
import { deskCard, deskEyebrow, deskMuted } from "./salesDeskStyles";
import { salesDeskRecordDate } from "./salesDeskSaleRecord";
import {
  SALES_DESK_PERFORMANCE_WINDOWS,
  salesDeskDaysOpen,
  salesDeskPerformanceChoices,
  selectSalesDeskPerformance,
  type SalesDeskPerformanceWindow,
} from "./salesDeskPerformanceCounts";
import { useSalesDeskPerformance } from "./useSalesDeskPerformance";

const selectClass =
  "mt-2 block w-full rounded-lg border border-[#dce3dc] bg-white px-4 py-3 font-normal text-[#142d2d] dark:border-zinc-700 dark:bg-zinc-900 dark:text-zinc-100";

function Count({ label, value }: { label: string; value: string }) {
  return (
    <section className={deskCard}>
      <p className="text-sm text-[#315d75] dark:text-sky-200">{label}</p>
      <p className={`mt-2 font-semibold tracking-[-1px] ${/^\d+$/.test(value) ? "text-[32px]" : "text-[18px]"}`}>{value}</p>
    </section>
  );
}

function FilterSelect({
  label,
  value,
  options,
  onChange,
}: {
  label: string;
  value: string;
  options: string[];
  onChange: (value: string) => void;
}) {
  return (
    <label className="block text-sm font-semibold">
      {label}
      <select aria-label={label} value={value} onChange={(event) => onChange(event.target.value)} className={selectClass}>
        <option value="">All</option>
        {options.map((option) => (
          <option key={option} value={option}>
            {option}
          </option>
        ))}
      </select>
    </label>
  );
}

export function SalesDeskPerformance({ stockRequests }: { stockRequests: number }) {
  const loaded = useSalesDeskPerformance();
  const [days, setDays] = useState<SalesDeskPerformanceWindow>(90);
  const [office, setOffice] = useState("");
  const [salesperson, setSalesperson] = useState("");
  const [model, setModel] = useState("");
  const choices = useMemo(() => salesDeskPerformanceChoices(loaded.rows), [loaded.rows]);
  const view = useMemo(
    () => selectSalesDeskPerformance(loaded.rows, { office, salesperson, model, days, now: Date.now() }),
    [loaded.rows, office, salesperson, model, days],
  );

  return (
    <div>
      <p className={deskEyebrow}>Inventory performance</p>
      <h1 className="mt-2 text-[32px] font-semibold leading-[1.15] tracking-[-1px]">What moves. What waits. What gets offered.</h1>
      <p className={`mt-3 max-w-[40rem] ${deskMuted}`}>
        The window follows when a sale was opened or last updated. Units on the lot stay in the list.
      </p>
      <section className={`${deskCard} mt-6`}>
        <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
          <FilterSelect label="Location" value={office} options={choices.offices} onChange={setOffice} />
          <FilterSelect label="Salesperson" value={salesperson} options={choices.people} onChange={setSalesperson} />
          <FilterSelect label="Style" value={model} options={choices.models} onChange={setModel} />
          <label className="block text-sm font-semibold">
            Time window
            <select
              aria-label="Time window"
              value={days}
              onChange={(event) => setDays(Number(event.target.value) as SalesDeskPerformanceWindow)}
              className={selectClass}
            >
              {SALES_DESK_PERFORMANCE_WINDOWS.map((window) => (
                <option key={window} value={window}>
                  {window} days
                </option>
              ))}
            </select>
          </label>
        </div>
        <p className={`mt-3 ${deskMuted}`}>Style is the product name on the sale. Location and salesperson are the office recorded on it.</p>
      </section>
      {loaded.error ? <p className="mt-4 text-sm text-red-800 dark:text-red-200">{loaded.error}</p> : null}
      {loaded.loading ? <p className={`mt-6 ${deskMuted}`}>Counting…</p> : null}
      {!loaded.loading && !loaded.error ? (
        <>
          <div className="mt-4 grid grid-cols-2 gap-4">
            <Count label="Opened" value={String(view.received)} />
            <Count label="Closed" value={String(view.closed)} />
            <Count label="Open" value={String(view.open)} />
            <Count label="On the lot" value={String(view.onLot)} />
          </div>
          <p className={`mt-4 ${deskMuted}`}>
            Mean days open for units on the lot: {view.meanDaysOpen == null ? "none yet" : view.meanDaysOpen.toFixed(1)}
          </p>
          <section className={`${deskCard} mt-6`}>
            <h2 className="text-[21px] font-semibold">Sales in this view</h2>
            <p className={`mt-2 ${deskMuted}`}>{view.table.length} sales opened, updated, or still on the lot.</p>
            {view.table.length === 0 ? <p className={`mt-4 ${deskMuted}`}>No sales in this view.</p> : null}
            <div className="mt-2 max-h-[28rem] overflow-y-auto">
              {view.table.map((row) => {
                const daysOpen = salesDeskDaysOpen(row.createdAt, Date.now());
                const place = [row.office, row.salesperson].filter(Boolean).join(" · ");
                return (
                  <article key={row.id} className="border-b border-[#dce3dc] py-4 last:border-b-0 dark:border-zinc-700">
                    <p className="text-sm font-semibold">
                      {row.customer}
                      <span className="ml-2 font-normal text-[#687772] dark:text-zinc-400">{row.id.slice(0, 8)}</span>
                    </p>
                    <p className="mt-1 text-xs text-[#687772] dark:text-zinc-400">
                      {salesDeskRecordDate(row.createdAt)}
                      {place ? ` · ${place}` : ""}
                    </p>
                    <p className="mt-1 text-xs text-[#687772] dark:text-zinc-400">
                      {[row.model, row.serial].filter(Boolean).join(" · ") || "No model or serial"}
                    </p>
                    <p className="mt-1 text-xs text-[#687772] dark:text-zinc-400">
                      {daysOpen == null ? "Opened date missing" : `${daysOpen} days open`}
                    </p>
                    <div className="mt-2 flex flex-wrap gap-2">
                      <span className="rounded-full bg-[#eef1e7] px-2.5 py-1 text-[11px] font-semibold text-[#164b43] dark:bg-zinc-800 dark:text-emerald-200">
                        {row.saleStatus}
                      </span>
                      <span className="rounded-full bg-[#e4edf2] px-2.5 py-1 text-[11px] font-semibold text-[#315d75] dark:bg-zinc-800 dark:text-sky-200">
                        {row.fulfillment}
                      </span>
                    </div>
                  </article>
                );
              })}
            </div>
          </section>
          <section className={`${deskCard} mt-6`}>
            <p className={deskEyebrow}>This session</p>
            <div className="mt-4 grid grid-cols-2 gap-4">
              <Count label="Issued quote versions" value="Not recorded" />
              <Count label="Quotes to closed sales" value="Not recorded" />
              <Count label="Shown to customer" value="Not recorded" />
              <Count label="Stock requests" value={String(stockRequests)} />
            </div>
            <p className={`mt-4 ${deskMuted}`}>
              Quote versions and shown-to-customer have no record yet. Stock requests count only this session.
            </p>
          </section>
        </>
      ) : null}
    </div>
  );
}
